import { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, Loader2, AlertTriangle, Play, Square, Sun, Moon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme, ThemeProvider } from '../contexts/ThemeContext';
import * as ort from 'onnxruntime-web';

// COCO class names for object detection
const COCO_CLASSES = [
    'person', 'bicycle', 'car', 'motorcycle', 'airplane', 'bus', 'train', 'truck', 'boat',
    'traffic light', 'fire hydrant', 'stop sign', 'parking meter', 'bench', 'bird', 'cat',
    'dog', 'horse', 'sheep', 'cow', 'elephant', 'bear', 'zebra', 'giraffe', 'backpack',
    'umbrella', 'handbag', 'tie', 'suitcase', 'frisbee', 'skis', 'snowboard', 'sports ball',
    'kite', 'baseball bat', 'baseball glove', 'skateboard', 'surfboard', 'tennis racket',
    'bottle', 'wine glass', 'cup', 'fork', 'knife', 'spoon', 'bowl', 'banana', 'apple',
    'sandwich', 'orange', 'broccoli', 'carrot', 'hot dog', 'pizza', 'donut', 'cake', 'chair',
    'couch', 'potted plant', 'bed', 'dining table', 'toilet', 'tv', 'laptop', 'mouse',
    'remote', 'keyboard', 'cell phone', 'microwave', 'oven', 'toaster', 'sink', 'refrigerator',
    'book', 'clock', 'vase', 'scissors', 'teddy bear', 'hair drier', 'toothbrush'
];

// Model configuration - YOLOv8n served locally from public folder
const MODEL_URL = '/models/yolov8n.onnx';
const INPUT_SIZE = 640;
const CONFIDENCE_THRESHOLD = 0.50;  // Standard threshold
const IOU_THRESHOLD = 0.45;

interface Detection {
    class: string;
    confidence: number;
    bbox: [number, number, number, number]; // [x1, y1, x2, y2]
}

interface TrackedObject {
    id: string; // unique id per object instance
    class: string;
    confidence: number;
    bbox: [number, number, number, number];
    firstSeen: number;
    lastSeen: number;
}

type AppState = 'checking' | 'unsupported' | 'ready' | 'loading' | 'running' | 'error';

// Top Navigation Bar component (same as WebGPU Chat)
function TopNavbar() {
    const { theme, toggleTheme } = useTheme();
    const isDark = theme === 'dark';

    return (
        <nav className="fixed top-0 left-0 right-0 z-50">
            <div
                className={`backdrop-blur-xl shadow-lg transition-all duration-300 ${isDark
                    ? 'bg-white/10 border-b border-white/10'
                    : 'bg-black/5 border-b border-black/10'
                    }`}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="flex items-center justify-between h-16 sm:h-20">
                        {/* Logo and Name - Links to home */}
                        <a
                            href="/"
                            className="flex items-center gap-3 group"
                        >
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br from-[var(--color-uf-blue)] to-[var(--color-uf-orange)] flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-105">
                                <span className="text-white text-sm sm:text-base font-semibold tracking-[0.2em]">VM</span>
                            </div>
                            <span className={`hidden sm:block font-medium tracking-wide ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                Vansh Mittal
                            </span>
                        </a>

                        {/* Center Title */}
                        <div className="absolute left-1/2 -translate-x-1/2">
                            <span className={`text-sm sm:text-base font-medium tracking-wide ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                                WebGPU Vision
                            </span>
                        </div>

                        {/* Theme Toggle */}
                        <button
                            onClick={toggleTheme}
                            className={`p-2.5 rounded-xl transition-all duration-300 ${isDark
                                ? 'hover:bg-white/10'
                                : 'hover:bg-black/10'
                                }`}
                            aria-label="Toggle theme"
                        >
                            <motion.div
                                key={theme}
                                initial={{ rotate: -90, opacity: 0 }}
                                animate={{ rotate: 0, opacity: 1 }}
                                exit={{ rotate: 90, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                {isDark ? (
                                    <Sun className={`w-5 h-5 ${isDark ? 'text-white/70 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`} />
                                ) : (
                                    <Moon className={`w-5 h-5 ${isDark ? 'text-white/70 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`} />
                                )}
                            </motion.div>
                        </button>
                    </div>
                </div>
            </div>
        </nav>
    );
}

function WebGPUVisionContent() {
    const [appState, setAppState] = useState<AppState>('checking');
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [statusText, setStatusText] = useState('Checking WebGPU support...');
    const [errorMessage, setErrorMessage] = useState('');
    const [detections, setDetections] = useState<Detection[]>([]);
    const [fps, setFps] = useState(0);
    const [isRunning, setIsRunning] = useState(false);

    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
    const sessionRef = useRef<ort.InferenceSession | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const animationFrameRef = useRef<number | null>(null);

    // Tracking refs for "sticky" and "delay" features
    const trackedObjectsRef = useRef<Map<string, TrackedObject>>(new Map());
    const nextIdRef = useRef(1);

    const { theme } = useTheme();
    const isDark = theme === 'dark';

    // Check WebGPU support on mount
    useEffect(() => {
        const checkWebGPU = async () => {
            try {
                if (!(navigator as any).gpu) {
                    setAppState('unsupported');
                    setErrorMessage('WebGPU is not supported in this browser. Try Chrome 113+ or Edge 113+.');
                    return;
                }

                const adapter = await (navigator as any).gpu.requestAdapter();
                if (!adapter) {
                    setAppState('unsupported');
                    setErrorMessage('No WebGPU adapter found. Your GPU may not be supported.');
                    return;
                }

                setAppState('ready');
                setStatusText('WebGPU is supported! Click below to start.');
            } catch (error) {
                console.error('WebGPU check failed:', error);
                setAppState('unsupported');
                setErrorMessage('Failed to initialize WebGPU.');
            }
        };

        checkWebGPU();
    }, []);

    // Initialize models
    const initializeModels = async () => {
        setAppState('loading');
        setLoadingProgress(0);
        setStatusText('Loading YOLOv8n model...');

        try {
            // Configure ONNX Runtime WASM path based on environment
            // Development: Vite serves node_modules
            // Production: Use CDN (must match installed package version)
            const isDev = (import.meta as any).env.DEV;
            ort.env.wasm.wasmPaths = isDev
                ? '/node_modules/onnxruntime-web/dist/'
                : 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.23.2/dist/';

            // Load YOLO model with progress tracking
            setLoadingProgress(0.1);
            setStatusText('Loading YOLOv8n model from cache...');

            const modelResponse = await fetch(MODEL_URL);
            if (!modelResponse.ok) {
                throw new Error('Failed to download model');
            }

            const contentLength = modelResponse.headers.get('content-length');
            const total = contentLength ? parseInt(contentLength, 10) : 20000000;
            let loaded = 0;

            const reader = modelResponse.body?.getReader();
            const chunks: Uint8Array[] = [];

            if (reader) {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    chunks.push(value);
                    loaded += value.length;
                    setLoadingProgress(0.1 + (loaded / total) * 0.6);
                }
            }

            const modelBuffer = new Uint8Array(loaded);
            let offset = 0;
            for (const chunk of chunks) {
                modelBuffer.set(chunk, offset);
                offset += chunk.length;
            }

            setLoadingProgress(0.75);
            setStatusText('Initializing YOLO model...');

            // Create inference session with WebGPU
            sessionRef.current = await ort.InferenceSession.create(modelBuffer.buffer, {
                executionProviders: ['webgpu', 'wasm'],
                graphOptimizationLevel: 'all',
            });


            setLoadingProgress(1.0);
            setStatusText('Models loaded! Starting camera...');

            // Start camera
            await startCamera();

            // Give time for state update and video element to mount
            await new Promise(resolve => setTimeout(resolve, 100));

            // Attach stream to video element now that it's mounted
            if (streamRef.current && videoRef.current) {
                videoRef.current.srcObject = streamRef.current;
                await videoRef.current.play();
                console.log('Video stream attached and playing');
            }

            setAppState('running');
        } catch (error) {
            console.error('Model loading failed:', error);
            setAppState('error');
            setErrorMessage(error instanceof Error ? error.message : 'Failed to load models');
        }
    };

    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { width: 640, height: 480 }
            });
            streamRef.current = stream;
            console.log('Camera stream created:', stream.getVideoTracks()[0].label);
        } catch (error) {
            throw new Error('Failed to access camera. Please allow camera permissions.');
        }
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
        setIsRunning(false);
    };
    // Preprocess image for YOLO
    const preprocessImage = (video: HTMLVideoElement, canvas: HTMLCanvasElement): Float32Array => {
        const ctx = canvas.getContext('2d')!;
        canvas.width = INPUT_SIZE;
        canvas.height = INPUT_SIZE;

        // Draw and resize video frame
        ctx.drawImage(video, 0, 0, INPUT_SIZE, INPUT_SIZE);

        const imageData = ctx.getImageData(0, 0, INPUT_SIZE, INPUT_SIZE);
        const data = imageData.data;

        // Convert to RGB and normalize to 0-1
        const inputArray = new Float32Array(3 * INPUT_SIZE * INPUT_SIZE);
        for (let i = 0; i < INPUT_SIZE * INPUT_SIZE; i++) {
            inputArray[i] = data[i * 4] / 255; // R
            inputArray[i + INPUT_SIZE * INPUT_SIZE] = data[i * 4 + 1] / 255; // G
            inputArray[i + 2 * INPUT_SIZE * INPUT_SIZE] = data[i * 4 + 2] / 255; // B
        }

        return inputArray;
    };

    // Non-maximum suppression
    const nms = (boxes: Detection[]): Detection[] => {
        const sorted = [...boxes].sort((a, b) => b.confidence - a.confidence);
        const selected: Detection[] = [];

        while (sorted.length > 0) {
            const current = sorted.shift()!;
            selected.push(current);

            for (let i = sorted.length - 1; i >= 0; i--) {
                if (calculateIoU(current.bbox, sorted[i].bbox) > IOU_THRESHOLD) {
                    sorted.splice(i, 1);
                }
            }
        }

        return selected;
    };

    const calculateIoU = (box1: [number, number, number, number], box2: [number, number, number, number]): number => {
        const x1 = Math.max(box1[0], box2[0]);
        const y1 = Math.max(box1[1], box2[1]);
        const x2 = Math.min(box1[2], box2[2]);
        const y2 = Math.min(box1[3], box2[3]);

        const intersection = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
        const area1 = (box1[2] - box1[0]) * (box1[3] - box1[1]);
        const area2 = (box2[2] - box2[0]) * (box2[3] - box2[1]);
        const union = area1 + area2 - intersection;

        return intersection / union;
    };

    // Process YOLO output
    const processOutput = (output: ort.Tensor, videoWidth: number, videoHeight: number): Detection[] => {
        const data = output.data as Float32Array;



        const [batch, numClasses_plus_4, numBoxes] = output.dims;
        const numClasses = numClasses_plus_4 - 4;



        const detections: Detection[] = [];

        for (let i = 0; i < numBoxes; i++) {
            // Get box coordinates (cx, cy, w, h)
            const cx = data[0 * numBoxes + i];
            const cy = data[1 * numBoxes + i];
            const w = data[2 * numBoxes + i];
            const h = data[3 * numBoxes + i];

            // Find best class
            let maxConf = 0;
            let maxClass = 0;
            for (let c = 0; c < numClasses; c++) {
                const conf = data[(4 + c) * numBoxes + i];
                if (conf > maxConf) {
                    maxConf = conf;
                    maxClass = c;
                }
            }



            if (maxConf > CONFIDENCE_THRESHOLD) {
                // Convert to corner coordinates and scale to video size
                const scaleX = videoWidth / INPUT_SIZE;
                const scaleY = videoHeight / INPUT_SIZE;

                detections.push({
                    class: COCO_CLASSES[maxClass] || `class_${maxClass}`,
                    confidence: maxConf,
                    bbox: [
                        (cx - w / 2) * scaleX,
                        (cy - h / 2) * scaleY,
                        (cx + w / 2) * scaleX,
                        (cy + h / 2) * scaleY
                    ]
                });
            }
        }


        return nms(detections);
    };

    // Draw bounding boxes
    const drawDetections = (ctx: CanvasRenderingContext2D, detections: Detection[]) => {
        if (!ctx) return;
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

        if (detections.length > 0) {
            console.log(`🎨 Drawing ${detections.length} bounding boxes`);
        }

        detections.forEach(det => {
            const [x1, y1, x2, y2] = det.bbox;

            // Draw box
            ctx.strokeStyle = '#FA4616';
            ctx.lineWidth = 4; // Thicker line
            ctx.lineJoin = 'round';
            ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);

            // Add subtle glow
            ctx.shadowColor = 'rgba(250, 70, 22, 0.5)';
            ctx.shadowBlur = 10;
            ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
            ctx.shadowBlur = 0;

            // Draw label
            const label = `${det.class} ${(det.confidence * 100).toFixed(0)}%`;
            ctx.font = '14px sans-serif';
            const textWidth = ctx.measureText(label).width;

            ctx.fillStyle = '#FA4616';
            ctx.fillRect(x1, y1 - 20, textWidth + 8, 20);

            ctx.fillStyle = 'white';
            ctx.fillText(label, x1 + 4, y1 - 6);
        });
    };



    // Main detection loop
    const runDetection = useCallback(async () => {
        if (!sessionRef.current || !videoRef.current || !canvasRef.current || !overlayCanvasRef.current) {
            return;
        }

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const overlayCanvas = overlayCanvasRef.current;

        if (!video.videoWidth || !video.videoHeight) {
            animationFrameRef.current = requestAnimationFrame(runDetection);
            return;
        }

        const overlayCtx = overlayCanvas.getContext('2d');
        if (!overlayCtx) return;

        // Match overlay to video internal resolution
        if (overlayCanvas.width !== video.videoWidth || overlayCanvas.height !== video.videoHeight) {
            overlayCanvas.width = video.videoWidth;
            overlayCanvas.height = video.videoHeight;
        }

        const startTime = performance.now();

        try {
            // Preprocess frame
            const inputData = preprocessImage(video, canvas);
            const inputTensor = new ort.Tensor('float32', inputData, [1, 3, INPUT_SIZE, INPUT_SIZE]);

            // Run inference
            const outputs = await sessionRef.current.run({ images: inputTensor });
            const output = outputs[Object.keys(outputs)[0]];

            // Process detections
            const currentDetections = processOutput(output, video.videoWidth, video.videoHeight);

            // 1. Real-time Bounding Boxes: Show immediately if confidence > 75%
            const realtimeDetections = currentDetections.filter(d => d.confidence > 0.75);
            drawDetections(overlayCtx, realtimeDetections);

            // 2. Sidebar List: Sticky/Delayed Tracking logic
            const now = Date.now();
            const activeTrackers = trackedObjectsRef.current;

            // Match new detections to existing trackers
            currentDetections.forEach(det => {
                let bestMatchId: string | null = null;
                let bestIoU = 0;

                for (const [id, tracker] of activeTrackers) {
                    if (tracker.class === det.class) {
                        const iou = calculateIoU(det.bbox, tracker.bbox);
                        if (iou > 0.3 && iou > bestIoU) {
                            bestIoU = iou;
                            bestMatchId = id;
                        }
                    }
                }

                if (bestMatchId) {
                    const tracker = activeTrackers.get(bestMatchId)!;
                    tracker.lastSeen = now;
                    tracker.bbox = det.bbox;
                    tracker.confidence = det.confidence;
                } else {
                    const newId = `obj_${nextIdRef.current++}`;
                    activeTrackers.set(newId, {
                        id: newId,
                        class: det.class,
                        confidence: det.confidence,
                        bbox: det.bbox,
                        firstSeen: now,
                        lastSeen: now
                    });
                }
            });

            // Filter and Cleanup trackers for the sidebar list
            const displayDetections: Detection[] = [];
            const idsToRemove: string[] = [];

            activeTrackers.forEach((tracker, id) => {
                // Remove if not seen for 2 seconds (Sticky persistence)
                if (now - tracker.lastSeen > 2000) {
                    idsToRemove.push(id);
                    return;
                }

                // Show in list only if tracked for at least 1 second (Confirmation delay)
                if (now - tracker.firstSeen > 1000) {
                    displayDetections.push({
                        class: tracker.class,
                        confidence: tracker.confidence,
                        bbox: tracker.bbox
                    });
                }
            });

            idsToRemove.forEach(id => activeTrackers.delete(id));

            // Update sidebar list
            setDetections(displayDetections);

            // Calculate FPS
            const endTime = performance.now();
            setFps(Math.round(1000 / (endTime - startTime)));
        } catch (error) {
            console.error('Detection error:', error);
        }

        // Continue loop
        if (isRunning) {
            animationFrameRef.current = requestAnimationFrame(runDetection);
        }
    }, [isRunning]);

    // Attach video stream when state changes to running
    useEffect(() => {
        if (appState === 'running' && streamRef.current && videoRef.current && !videoRef.current.srcObject) {
            videoRef.current.srcObject = streamRef.current;
            videoRef.current.play().catch(err => console.error('Video play error:', err));
            console.log('Video stream attached in useEffect');
        }
    }, [appState]);

    // Start/stop detection loop
    useEffect(() => {
        if (isRunning && appState === 'running') {
            animationFrameRef.current = requestAnimationFrame(runDetection);
        }
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [isRunning, appState, runDetection]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopCamera();
        };
    }, []);

    // Theme-aware colors
    const colors = {
        containerBg: isDark ? 'rgba(18, 18, 25, 0.95)' : 'rgba(247, 245, 239, 0.98)',
        cardBg: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
        border: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
        text: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(30, 30, 30, 0.9)',
        textMuted: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(30, 30, 30, 0.5)',
    };

    // Render loading/setup states
    if (appState !== 'running') {
        return (
            <>
                <TopNavbar />
                <div
                    className="min-h-screen flex items-center justify-center p-4 pt-24 sm:pt-28"
                    style={{ backgroundColor: isDark ? '#0a0a0f' : '#f7f5ef' }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-md w-full p-8 rounded-3xl backdrop-blur-xl"
                        style={{
                            backgroundColor: colors.containerBg,
                            border: `1px solid ${colors.border}`,
                        }}
                    >
                        {/* Header */}
                        <div className="flex items-center gap-4 mb-6">
                            <div
                                className="w-12 h-12 rounded-2xl flex items-center justify-center"
                                style={{
                                    background: 'linear-gradient(135deg, var(--color-uf-blue) 0%, var(--color-uf-orange) 100%)',
                                }}
                            >
                                <Camera className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-semibold" style={{ color: colors.text }}>
                                    WebGPU Vision
                                </h1>
                                <p className="text-sm" style={{ color: colors.textMuted }}>
                                    YOLOv8n
                                </p>
                            </div>
                        </div>

                        {/* Status Content */}
                        {appState === 'checking' && (
                            <div className="flex flex-col items-center py-8">
                                <Loader2 className="w-8 h-8 animate-spin mb-4" style={{ color: 'var(--color-uf-blue)' }} />
                                <p style={{ color: colors.text }}>{statusText}</p>
                            </div>
                        )}

                        {appState === 'unsupported' && (
                            <div className="text-center py-4">
                                <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
                                <p className="mb-2" style={{ color: colors.text }}>WebGPU Not Supported</p>
                                <p className="text-sm" style={{ color: colors.textMuted }}>{errorMessage}</p>
                            </div>
                        )}

                        {appState === 'ready' && (
                            <div className="space-y-4">
                                <div
                                    className="p-4 rounded-xl text-sm"
                                    style={{ backgroundColor: colors.cardBg, color: colors.textMuted }}
                                >
                                    <p className="mb-2">✓ WebGPU is supported!</p>
                                    <p className="mb-2">This demo will:</p>
                                    <ul className="list-disc list-inside space-y-1 text-xs">
                                        <li>Load YOLOv8n model (~12MB)</li>
                                        <li>Access your camera for real-time detection</li>
                                        <li>Delay of 1s to confirm objects</li>
                                        <li>Results stick for 2s for stability</li>
                                    </ul>
                                </div>
                                <button
                                    onClick={initializeModels}
                                    className="w-full py-4 px-6 rounded-2xl font-medium text-white transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2"
                                    style={{
                                        background: 'linear-gradient(135deg, var(--color-uf-blue) 0%, var(--color-uf-orange) 100%)',
                                    }}
                                >
                                    <Camera className="w-5 h-5" />
                                    Start Vision Demo
                                </button>
                            </div>
                        )}

                        {appState === 'loading' && (
                            <div className="space-y-4">
                                <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.cardBg }}>
                                    <motion.div
                                        className="h-full rounded-full"
                                        style={{
                                            background: 'linear-gradient(90deg, var(--color-uf-blue), var(--color-uf-orange))',
                                        }}
                                        initial={{ width: 0 }}
                                        animate={{ width: `${loadingProgress * 100}%` }}
                                    />
                                </div>
                                <p className="text-sm text-center" style={{ color: colors.textMuted }}>
                                    {Math.round(loadingProgress * 100)}%
                                </p>
                                <p className="text-sm text-center" style={{ color: colors.text }}>
                                    {statusText}
                                </p>
                            </div>
                        )}

                        {appState === 'error' && (
                            <div className="text-center py-4">
                                <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-500" />
                                <p className="mb-4" style={{ color: colors.text }}>{errorMessage}</p>
                                <button
                                    onClick={() => setAppState('ready')}
                                    className="py-2 px-4 rounded-xl bg-[var(--color-uf-blue)] text-white font-medium hover:opacity-90 transition-opacity"
                                >
                                    Try Again
                                </button>
                            </div>
                        )}
                    </motion.div>
                </div>
            </>
        );
    }

    return (
        <>
            <TopNavbar />
            <div
                className="min-h-screen p-4 pt-24 sm:pt-28"
                style={{ backgroundColor: isDark ? '#0a0a0f' : '#f7f5ef' }}
            >
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-3 gap-6">
                        {/* Video Feed */}
                        <div className="lg:col-span-2">
                            <div
                                className="rounded-3xl overflow-hidden backdrop-blur-xl relative"
                                style={{
                                    backgroundColor: colors.containerBg,
                                    border: `1px solid ${colors.border}`,
                                }}
                            >
                                <div className="relative">
                                    <video
                                        ref={videoRef}
                                        className="w-full"
                                        autoPlay
                                        playsInline
                                        muted
                                    />
                                    <canvas
                                        ref={overlayCanvasRef}
                                        className="absolute top-0 left-0 w-full h-full pointer-events-none"
                                        style={{ zIndex: 10 }}
                                    />
                                    {/* Hidden canvas for preprocessing */}
                                    <canvas ref={canvasRef} className="hidden" />

                                    {/* FPS Counter */}
                                    <div className="absolute top-4 left-4 px-3 py-1 rounded-lg bg-black/50 text-white text-sm font-mono">
                                        {fps} FPS
                                    </div>
                                </div>

                                {/* Controls */}
                                <div className="p-4 flex gap-3">
                                    <button
                                        onClick={() => setIsRunning(!isRunning)}
                                        className={`flex-1 py-3 px-6 rounded-xl font-medium text-white transition-all duration-300 flex items-center justify-center gap-2 ${isRunning ? 'bg-red-500 hover:bg-red-600' : 'bg-[var(--color-uf-blue)] hover:opacity-90'
                                            }`}
                                    >
                                        {isRunning ? (
                                            <>
                                                <Square className="w-5 h-5" />
                                                Stop Detection
                                            </>
                                        ) : (
                                            <>
                                                <Play className="w-5 h-5" />
                                                Start Detection
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Detections Panel */}
                        <div
                            className="rounded-3xl p-6 backdrop-blur-xl"
                            style={{
                                backgroundColor: colors.containerBg,
                                border: `1px solid ${colors.border}`,
                            }}
                        >
                            <h2 className="text-lg font-semibold mb-4" style={{ color: colors.text }}>
                                Detected Objects
                            </h2>

                            {detections.length === 0 ? (
                                <p className="text-sm" style={{ color: colors.textMuted }}>
                                    {isRunning ? 'No objects detected...' : 'Click "Start Detection" to begin'}
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {detections.map((det, i) => (
                                        <div
                                            key={i}
                                            className="flex items-center justify-between p-3 rounded-xl"
                                            style={{ backgroundColor: colors.cardBg }}
                                        >
                                            <span className="font-medium capitalize" style={{ color: colors.text }}>
                                                {det.class}
                                            </span>
                                            <span
                                                className="text-sm px-2 py-1 rounded-lg"
                                                style={{
                                                    backgroundColor: 'var(--color-uf-orange)',
                                                    color: 'white',
                                                }}
                                            >
                                                {(det.confidence * 100).toFixed(0)}%
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Stats */}
                            <div className="mt-6 pt-4 border-t" style={{ borderColor: colors.border }}>
                                <div className="grid grid-cols-2 gap-4 text-center">
                                    <div>
                                        <p className="text-2xl font-bold" style={{ color: colors.text }}>
                                            {detections.length}
                                        </p>
                                        <p className="text-xs" style={{ color: colors.textMuted }}>
                                            Objects
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold" style={{ color: colors.text }}>
                                            {fps}
                                        </p>
                                        <p className="text-xs" style={{ color: colors.textMuted }}>
                                            FPS
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

// Wrap with ThemeProvider for standalone page
export default function WebGPUVision() {
    return (
        <ThemeProvider>
            <WebGPUVisionContent />
        </ThemeProvider>
    );
}
